import { ADMIN_API_URL, apiFetch } from './apiConfig';

// ============================================
// ADMIN API SERVICE LAYER
// All calls go to the new admin backend (port 5002)
// ============================================

export const adminAPI = {
    // ── Data Fetching ────────────────────────────
    users: (search = '') => apiFetch(`${ADMIN_API_URL}/allusers${search ? `?search=${encodeURIComponent(search)}` : ''}`).then(r => r.json()),
    trips: () => apiFetch(`${ADMIN_API_URL}/alltrips`).then(r => r.json()),
    expenses: () => apiFetch(`${ADMIN_API_URL}/allexpenses`).then(r => r.json()),
    messages: () => apiFetch(`${ADMIN_API_URL}/allmessages`).then(r => r.json()),

    // ── Single User ──────────────────────────────
    getUser: (userId) => apiFetch(`${ADMIN_API_URL}/user/${userId}`).then(r => r.json()),
    updateUser: (userId, data) => apiFetch(`${ADMIN_API_URL}/user/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }).then(r => r.json()),
    createUser: (data) => apiFetch(`${ADMIN_API_URL}/user/create`, {
        method: 'POST',
        body: JSON.stringify(data)
    }).then(r => r.json()),
    deleteUser: (userId) => apiFetch(`${ADMIN_API_URL}/user/${userId}`, { method: 'DELETE' }).then(r => r.json()),
    resetPassword: (userId, newPassword) => apiFetch(`${ADMIN_API_URL}/user/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword })
    }).then(r => r.json()),

    // ── Trips & Expenses ─────────────────────────
    deleteTrip: (tripId) => apiFetch(`${ADMIN_API_URL}/trip/${tripId}`, { method: 'DELETE' }).then(r => r.json()),
    deleteMessage: (messageId) => apiFetch(`${ADMIN_API_URL}/message/${messageId}`, { method: 'DELETE' }).then(r => r.json()),

    // ── Bulk Operations ──────────────────────────
    bulkOperation: (action, userIds) => apiFetch(`${ADMIN_API_URL}/bulk-operations`, {
        method: 'POST',
        body: JSON.stringify({ action, userIds })
    }).then(r => r.json()),

    // ── Exports ──────────────────────────────────
    exportUsers: () => apiFetch(`${ADMIN_API_URL}/export/users`).then(r => r.blob()),
    exportTrips: () => apiFetch(`${ADMIN_API_URL}/export/trips`).then(r => r.blob()),
    exportExpenses: () => apiFetch(`${ADMIN_API_URL}/export/expenses`).then(r => r.blob()),

    // ── Activity & Audit Logs ────────────────────
    activityLogs: (userId) => apiFetch(`${ADMIN_API_URL}/activity-logs/${userId}`).then(r => r.json()),
    auditLogs: (limit = 100) => apiFetch(`${ADMIN_API_URL}/audit-logs?limit=${limit}`).then(r => r.json()),

    // ── Analytics ────────────────────────────────
    overview: () => apiFetch(`${ADMIN_API_URL}/analytics/overview`).then(r => r.json()),
    userGrowth: () => apiFetch(`${ADMIN_API_URL}/analytics/user-growth`).then(r => r.json()),
    revenue: () => apiFetch(`${ADMIN_API_URL}/analytics/revenue`).then(r => r.json()),
    tripStatistics: () => apiFetch(`${ADMIN_API_URL}/analytics/trip-statistics`).then(r => r.json()),
    expenseBreakdown: () => apiFetch(`${ADMIN_API_URL}/analytics/expense-breakdown`).then(r => r.json()),
    engagement: () => apiFetch(`${ADMIN_API_URL}/analytics/engagement`).then(r => r.json()),

    // ── System ───────────────────────────────────
    health: () => apiFetch(`${ADMIN_API_URL}/system/health`).then(r => r.json()),
    dbStats: () => apiFetch(`${ADMIN_API_URL}/system/db-stats`).then(r => r.json()),
    optimizeDB: () => apiFetch(`${ADMIN_API_URL}/system/optimize-db`, { method: 'POST' }).then(r => r.json()),
    clearCache: () => apiFetch(`${ADMIN_API_URL}/system/clear-cache`, { method: 'POST' }).then(r => r.json()),
    backup: () => apiFetch(`${ADMIN_API_URL}/system/backup`, { method: 'POST' }).then(r => r.json()),

    // ── Security ─────────────────────────────────
    admins: () => apiFetch(`${ADMIN_API_URL}/admins`).then(r => r.json()),
    promoteUser: (userId) => apiFetch(`${ADMIN_API_URL}/promote/${userId}`, { method: 'POST' }).then(r => r.json()),
    demoteUser: (userId) => apiFetch(`${ADMIN_API_URL}/demote/${userId}`, { method: 'POST' }).then(r => r.json()),
    suspendUser: (userId) => apiFetch(`${ADMIN_API_URL}/suspend/${userId}`, { method: 'POST' }).then(r => r.json()),
    unsuspendUser: (userId) => apiFetch(`${ADMIN_API_URL}/unsuspend/${userId}`, { method: 'POST' }).then(r => r.json()),
    loginHistory: () => apiFetch(`${ADMIN_API_URL}/security/login-history`).then(r => r.json()),

    // ── Data Management ──────────────────────────
    findDuplicates: () => apiFetch(`${ADMIN_API_URL}/data/find-duplicates`).then(r => r.json()),
    cleanupInactive: (months) => apiFetch(`${ADMIN_API_URL}/data/cleanup-inactive`, {
        method: 'POST',
        body: JSON.stringify({ months })
    }).then(r => r.json()),
    archive: (yearsOld) => apiFetch(`${ADMIN_API_URL}/data/archive`, {
        method: 'POST',
        body: JSON.stringify({ yearsOld })
    }).then(r => r.json()),
    validateData: () => apiFetch(`${ADMIN_API_URL}/data/validate`, { method: 'POST' }).then(r => r.json()),
    recoverData: () => apiFetch(`${ADMIN_API_URL}/data/recover`, { method: 'POST' }).then(r => r.json()),

    // ── Communication ────────────────────────────
    massEmail: (userIds, subject, body) => apiFetch(`${ADMIN_API_URL}/communication/mass-email`, {
        method: 'POST',
        body: JSON.stringify({ userIds, subject, body })
    }).then(r => r.json()),
    sendNotification: (userIds, title, message) => apiFetch(`${ADMIN_API_URL}/communication/send-notification`, {
        method: 'POST',
        body: JSON.stringify({ userIds, title, message })
    }).then(r => r.json()),

    // ── Announcements ────────────────────────────
    getAnnouncements: () => apiFetch(`${ADMIN_API_URL}/announcements`).then(r => r.json()),
    createAnnouncement: (title, content, type) => apiFetch(`${ADMIN_API_URL}/announcements`, {
        method: 'POST',
        body: JSON.stringify({ title, content, type })
    }).then(r => r.json()),
    deleteAnnouncement: (id) => apiFetch(`${ADMIN_API_URL}/announcements/${id}`, { method: 'DELETE' }).then(r => r.json()),

    // ── Settings ─────────────────────────────────
    getSettings: () => apiFetch(`${ADMIN_API_URL}/settings`).then(r => r.json()),
    updateSetting: (key, value) => apiFetch(`${ADMIN_API_URL}/settings/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ value })
    }).then(r => r.json()),

    // ── Maintenance ──────────────────────────────
    getMaintenanceStatus: () => apiFetch(`${ADMIN_API_URL}/maintenance`).then(r => r.json()),
    setMaintenanceMode: (enabled, message) => apiFetch(`${ADMIN_API_URL}/maintenance`, {
        method: 'PUT',
        body: JSON.stringify({ enabled, message })
    }).then(r => r.json()),

    // ── Integrations ─────────────────────────────
    integrationsStatus: () => apiFetch(`${ADMIN_API_URL}/integrations/status`).then(r => r.json()),

    // ── Scheduled Tasks ──────────────────────────
    getScheduledTasks: () => apiFetch(`${ADMIN_API_URL}/scheduled-tasks`).then(r => r.json()),
    createScheduledTask: (taskType, payload, scheduledFor) => apiFetch(`${ADMIN_API_URL}/scheduled-tasks`, {
        method: 'POST',
        body: JSON.stringify({ taskType, payload, scheduledFor })
    }).then(r => r.json()),
    deleteScheduledTask: (id) => apiFetch(`${ADMIN_API_URL}/scheduled-tasks/${id}`, { method: 'DELETE' }).then(r => r.json()),
};

// Helper to download CSV files
export const downloadCSV = async (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};
